// SPDX-FileCopyrightText: Copyright Orangebot, Inc. and Medplum contributors
// SPDX-License-Identifier: Apache-2.0
import { Button, Group, Modal, Select, Stack, Textarea, TextInput } from '@mantine/core';
import { DateTimePicker } from '@mantine/dates';
import { useForm } from '@mantine/form';
import { showNotification } from '@mantine/notifications';
import { createReference, normalizeErrorString } from '@medplum/core';
import type { Communication, Patient } from '@medplum/fhirtypes';
import { useMedplum, useMedplumProfile } from '@medplum/react';
import { IconCheck } from '@tabler/icons-react';
import { type JSX, useState } from 'react';
import { dispatchCommunicationCreated } from '../../../utils/patient-updates';
import { withTenantScope } from '../../../utils/tenant';

const PARTICIPATION_MODE_SYSTEM = 'http://terminology.hl7.org/CodeSystem/v3-ParticipationMode';
const POLARIS_COMMUNICATION_TAG_SYSTEM = 'http://polarissleep.com/communication/source';
const MANUAL_ENTRY_TAG_CODE = 'manual-entry';

/**
 * Options the provider picks from when logging an off-platform communication.
 * Codes map to FHIR v3-ParticipationMode where available; otherwise a
 * Polaris-scoped code is used so the existing `getMedium` mapping in the tab
 * classifies it correctly.
 */
export const MEDIUM_OPTIONS = [
  { value: 'phone', label: 'Phone Call', system: PARTICIPATION_MODE_SYSTEM, display: 'Telephone' },
  { value: 'email', label: 'Email', system: PARTICIPATION_MODE_SYSTEM, display: 'Email' },
  { value: 'sms', label: 'SMS / Text', system: PARTICIPATION_MODE_SYSTEM, display: 'SMS message' },
  { value: 'video', label: 'Video Call', system: PARTICIPATION_MODE_SYSTEM, display: 'Video Call' },
];

function toDateTimePickerValue(date: Date): string {
  const offset = date.getTimezoneOffset() * 60000;
  return new Date(date.getTime() - offset).toISOString().slice(0, 16).replace('T', ' ');
}

function toIsoDateTime(value: string | null): string {
  const normalized = value ? value.replace(' ', 'T') : '';
  const date = normalized ? new Date(normalized) : new Date();
  return Number.isFinite(date.getTime()) ? date.toISOString() : new Date().toISOString();
}

interface FormValues {
  medium: string;
  type: string;
  sent: string | null;
  content: string;
}

export interface NewCommunicationModalProps {
  patient: Patient;
  opened: boolean;
  onClose: () => void;
  onSaved: (communication: Communication) => void;
}

export function NewCommunicationModal({ patient, opened, onClose, onSaved }: NewCommunicationModalProps): JSX.Element {
  const medplum = useMedplum();
  const profile = useMedplumProfile();
  const [saving, setSaving] = useState(false);

  const form = useForm<FormValues>({
    initialValues: {
      medium: 'phone',
      type: '',
      sent: toDateTimePickerValue(new Date()),
      content: '',
    },
    validate: {
      medium: (v) => (v ? null : 'Medium is required'),
      content: (v) => (v.trim() ? null : 'Summary is required'),
      sent: (v) => (v ? null : 'Date / time is required'),
    },
  });

  const handleClose = (): void => {
    form.reset();
    onClose();
  };

  const handleSave = form.onSubmit(async (values) => {
    setSaving(true);
    try {
      const mediumOption = MEDIUM_OPTIONS.find((o) => o.value === values.medium) ?? MEDIUM_OPTIONS[0];
      const sentIso = toIsoDateTime(values.sent);

      const communication: Communication = {
        resourceType: 'Communication',
        status: 'completed',
        sent: sentIso,
        subject: createReference(patient),
        recipient: [createReference(patient)],
        sender: profile ? createReference(profile) : undefined,
        medium: [
          {
            coding: [{ system: mediumOption.system, code: mediumOption.value, display: mediumOption.display }],
            text: mediumOption.label,
          },
        ],
        category: values.type.trim()
          ? [{ coding: [{ display: values.type.trim() }], text: values.type.trim() }]
          : undefined,
        payload: [{ contentString: values.content.trim() }],
        meta: {
          tag: [{ system: POLARIS_COMMUNICATION_TAG_SYSTEM, code: MANUAL_ENTRY_TAG_CODE }],
        },
      };

      const saved = await medplum.createResource<Communication>(withTenantScope(communication));
      dispatchCommunicationCreated(saved);
      onSaved(saved);
      form.reset();
      onClose();
      showNotification({ color: 'green', icon: <IconCheck size={16} />, message: 'Communication logged' });
    } catch (err) {
      showNotification({ color: 'red', message: normalizeErrorString(err), autoClose: false });
    } finally {
      setSaving(false);
    }
  });

  return (
    <Modal opened={opened} onClose={handleClose} title="Log a Communication" size="lg" centered>
      <Stack gap="md">
        <Select
          label="Medium"
          data={MEDIUM_OPTIONS.map((o) => ({ value: o.value, label: o.label }))}
          withAsterisk
          {...form.getInputProps('medium')}
        />
        <TextInput
          label="Type"
          placeholder="e.g. Check-in, Insurance question, Scheduling"
          {...form.getInputProps('type')}
        />
        <DateTimePicker
          label="Date & Time"
          placeholder="Select when this occurred"
          withAsterisk
          clearable={false}
          maxDate={new Date()}
          {...form.getInputProps('sent')}
        />
        <Textarea
          label="Summary"
          placeholder="What was discussed…"
          withAsterisk
          minRows={4}
          autosize
          {...form.getInputProps('content')}
        />
        <Group justify="flex-end">
          <Button variant="default" onClick={handleClose} disabled={saving}>
            Cancel
          </Button>
          <Button onClick={() => handleSave()} loading={saving}>
            Save
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}
